import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { ArrowLeft, Clock, Calendar as CalendarIcon, CreditCard, Video } from "lucide-react";
import { format, addDays, isBefore, startOfDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, formType, formData: passedFormData } = location.state || {};

  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: passedFormData?.name || "",
    email: passedFormData?.email || "",
    wikipedia_page: passedFormData?.wikipedia_page_url || passedFormData?.topic || "",
    notes: ""
  });

  // Get available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/booking/slots?date=${date}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Failed to load available times");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({ ...prev, [name]: value }));
  };

  const handleBookConsultation = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    setIsBooking(true);
    try {
      // First create the booking
      const bookingResponse = await axios.post(`${API}/booking/create`, {
        name: bookingData.name,
        email: bookingData.email,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        wikipedia_page: bookingData.wikipedia_page,
        notes: bookingData.notes,
        form_type: formType || "atopic"
      });

      if (bookingResponse.data.success) {
        // Create Stripe checkout
        const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
          amount: 150.00,
          booking_id: bookingResponse.data.booking_id,
          origin_url: window.location.origin
        });

        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        }
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to create booking. Please try again.");
    } finally {
      setIsBooking(false);
    }
  };

  // Disable past dates and weekends
  const disabledDays = (date) => {
    const today = startOfDay(new Date());
    const minDate = addDays(today, 1); // At least 1 day in advance
    return isBefore(date, minDate) || date.getDay() === 0 || date.getDay() === 6;
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm} EST`;
  };

  return (
    <div className="min-h-screen bg-background py-20 px-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <Link to="/" className="inline-flex items-center text-text-secondary hover:text-accent mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <div className="flex items-center gap-4 mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
            alt="Ethical Wiki" 
            className="w-12 h-12"
            width="48"
            height="48"
          />
          <div>
            <h1 className="font-heading text-3xl text-text-primary">Book Your Consultation</h1>
            <p className="text-text-secondary">Schedule a $150 Zoom consultation with David King</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calendar Section */}
          <Card className="card-marketing">
            <CardContent className="p-6">
              <CardHeader className="p-0 mb-6">
                <CardTitle className="font-heading text-xl text-text-primary flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-accent" />
                  Select a Date
                </CardTitle>
              </CardHeader>
              
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={disabledDays}
                className="rounded-sm border border-border bg-background-subtle p-3"
                data-testid="booking-calendar"
              />
              
              {/* Time Slots */}
              {selectedDate && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6"
                >
                  <h3 className="font-heading text-lg text-text-primary mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-accent" />
                    Available Times for {format(selectedDate, "MMMM d, yyyy")}
                  </h3>
                  
                  {isLoading ? (
                    <div className="text-text-muted">Loading available times...</div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          variant={selectedTime === slot ? "default" : "outline"}
                          onClick={() => handleTimeSelect(slot)}
                          className={selectedTime === slot ? "btn-primary" : "btn-secondary"}
                          data-testid={`time-slot-${slot}`}
                        >
                          {formatTime(slot)}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-text-muted">No available times for this date</div>
                  )}
                </motion.div>
              )}
            </CardContent>
          </Card>

          {/* Booking Details Section */}
          <Card className="card-marketing">
            <CardContent className="p-6">
              <CardHeader className="p-0 mb-6">
                <CardTitle className="font-heading text-xl text-text-primary flex items-center gap-2">
                  <Video className="w-5 h-5 text-accent" />
                  Consultation Details
                </CardTitle>
              </CardHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-text-primary">Your Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={bookingData.name}
                    onChange={handleInputChange}
                    className="input-marketing mt-2"
                    placeholder="John Doe"
                    data-testid="booking-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-text-primary">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={bookingData.email}
                    onChange={handleInputChange}
                    className="input-marketing mt-2"
                    placeholder="john@company.com"
                    data-testid="booking-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="wikipedia_page" className="text-text-primary">Wikipedia Page (if applicable)</Label>
                  <Input
                    id="wikipedia_page"
                    name="wikipedia_page"
                    value={bookingData.wikipedia_page}
                    onChange={handleInputChange}
                    className="input-marketing mt-2"
                    placeholder="https://en.wikipedia.org/wiki/..."
                    data-testid="booking-wiki-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes" className="text-text-primary">Notes for the Consultation</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={bookingData.notes}
                    onChange={handleInputChange}
                    className="input-marketing mt-2 min-h-[100px]"
                    placeholder="What would you like to discuss?"
                    data-testid="booking-notes-input"
                  />
                </div>

                {/* Summary */}
                <div className="border-t border-border pt-6 mt-6">
                  <div className="bg-background-subtle p-4 rounded-sm mb-6">
                    <h4 className="font-heading text-lg text-text-primary mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-text-secondary">
                        <span>Date:</span>
                        <span className="text-text-primary">
                          {selectedDate ? format(selectedDate, "MMMM d, yyyy") : "Not selected"}
                        </span>
                      </div>
                      <div className="flex justify-between text-text-secondary">
                        <span>Time:</span>
                        <span className="text-text-primary">
                          {selectedTime ? formatTime(selectedTime) : "Not selected"}
                        </span>
                      </div>
                      <div className="flex justify-between text-text-secondary">
                        <span>Duration:</span>
                        <span className="text-text-primary">1 hour</span>
                      </div>
                      <div className="flex justify-between text-accent font-bold text-lg pt-2 border-t border-border mt-2">
                        <span>Total:</span>
                        <span>$150.00</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleBookConsultation}
                    disabled={!selectedDate || !selectedTime || isBooking}
                    className="btn-primary w-full gold-glow-hover"
                    data-testid="confirm-booking-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {isBooking ? "Processing..." : "Proceed to Payment"}
                  </Button>
                  
                  <p className="text-text-muted text-xs text-center mt-4">
                    You will be redirected to Stripe for secure payment
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
